// frontend/src/api.ts
import axios, { AxiosError } from 'axios';
import { Conversation, ExtendedMessage, PaginatedConversations } from './types';
import { Socket } from 'socket.io-client';
import { initializeSocket } from './socket';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'https://typia.app';

// Créer une instance d'axios avec une configuration de base
const apiClient = axios.create({
    baseURL: BACKEND_URL,
    withCredentials: true,
});

// Vérifier l'état d'authentification
export const checkAuth = async (): Promise<{ message: string; userId: string } | null> => {
    try {
        const response = await apiClient.get<{ message: string; userId: string }>('/api/check-auth');
        console.log('Réponse de /check-auth:', response.data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
            console.log('Utilisateur non authentifié');
            return null;
        }
        console.error('Erreur lors de la vérification de l\'authentification:', error);
        throw error;
    }
};

// Récupérer toutes les conversations de l'utilisateur avec pagination
export const getConversations = async (page = 1, limit = 20): Promise<PaginatedConversations> => {
    try {
        const response = await apiClient.get<PaginatedConversations>(`/api/conversations?page=${page}&limit=${limit}`);
        console.log('Réponse de /conversations:', response.data);
        const conversations = response.data?.conversations || [];
        if (!Array.isArray(conversations)) {
            throw new Error('Réponse des conversations invalide');
        }
        return {
            conversations,
            pagination: response.data?.pagination || { total: 0, page, pages: 0 }
        };
    } catch (error) {
        console.error('Error fetching conversations:', error);
        return { conversations: [], pagination: { total: 0, page, pages: 0 } };
    }
};

// Récupérer les conversations archivées
export const getArchivedConversations = async (page = 1, limit = 20): Promise<PaginatedConversations> => {
    try {
        const response = await apiClient.get<PaginatedConversations>(`/api/conversations/archived?page=${page}&limit=${limit}`);
        console.log('Réponse de /conversations/archived:', response.data);
        const conversations = response.data?.conversations || [];
        if (!Array.isArray(conversations)) {
            throw new Error('Réponse des conversations archivées invalide');
        }
        return {
            conversations,
            pagination: response.data?.pagination || { total: 0, page, pages: 0 }
        };
    } catch (error) {
        console.error('Error fetching archived conversations:', error);
        return { conversations: [], pagination: { total: 0, page, pages: 0 } };
    }
};

// Récupérer une conversation spécifique par ID
export const getConversation = async (id: string): Promise<Conversation | null> => {
    try {
        const response = await apiClient.get<{ data: Conversation }>(`/api/conversations/${id}`);
        console.log('Réponse de /conversations/:id:', response.data);
        return response.data.data;
    } catch (error) {
        console.error('Error fetching conversation:', error);
        return null;
    }
};

// Mettre à jour le titre d'une conversation
export const updateConversationTitle = async (id: string, title: string): Promise<Conversation | null> => {
    try {
        const response = await apiClient.put<{ conversation: Conversation }>(`/api/conversations/${id}/title`, { title });
        console.log('Réponse de mise à jour du titre:', response.data);
        return response.data.conversation;
    } catch (error) {
        console.error('Error updating conversation title:', error);
        return null;
    }
};

// Supprimer une conversation
export const deleteConversation = async (id: string): Promise<boolean> => {
    try {
        const response = await apiClient.delete<{ success: boolean }>(`/api/conversations/${id}`);
        console.log('Réponse de suppression:', response.data);
        return response.data.success;
    } catch (error) {
        console.error('Error deleting conversation:', error);
        return false;
    }
};

// Envoyer un message via HTTP (méthode alternative à WebSocket)
export const sendMessageHttp = async (
    message: string,
    selectedModel: string,
    conversationId?: string
): Promise<{ conversationId: string; message: string } | null> => {
    try {
        const response = await apiClient.post(`/api/conversations/send`, {
            content: message,
            model: selectedModel,
            conversationId
        });
        console.log('Réponse de /conversations/send:', response.data);
        return response.data;
    } catch (error) {
        console.error('Error sending message via HTTP:', error);
        return null;
    }
};

// Envoyer un message via WebSocket avec gestion du contexte de conversation
export const sendMessage = async (
    message: string,
    selectedModel: string,
    setMessages: (value: React.SetStateAction<ExtendedMessage[]>) => void,
    setInputValue: (value: string) => void,
    setIsLoading: (value: boolean) => void,
    setIsAuthenticated: (value: boolean) => void,
    socket: Socket | null,
    isAuthenticated: boolean,
    setIsSocketConnected: (value: boolean) => void,
    conversationId?: string
): Promise<void> => {
    const sanitizedMessage = message.trim().replace(/<script.*?>.*?<\/script>/gi, ''); // Protection contre XSS
    if (!sanitizedMessage || !socket) return;

    // Créer le message utilisateur
    const userMessage: ExtendedMessage = {
        sender: 'user',
        content: sanitizedMessage,
        type: 'text',
        timestamp: new Date().toISOString(),
        final: true,
        conversationId,
    };
    
    // Mettre à jour l'interface utilisateur
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    
    // Ajouter un indicateur de chargement
    setMessages((prev) => [
        ...prev, 
        { 
            sender: 'ia', 
            content: 'Composing...', 
            type: 'text', 
            modelName: selectedModel,
            conversationId
        }
    ]);

    try {
        // Vérifier la connexion WebSocket
        if (!socket.connected) {
            console.log('Socket not connected, trying to reconnect...');
            const { socket: newSocket } = await initializeSocket(
                isAuthenticated, 
                setIsSocketConnected, 
                setMessages, 
                setIsLoading
            );
            
            if (newSocket) {
                socket = newSocket;
            } else {
                throw new Error('Failed to connect socket');
            }
        }
        
        // Émettre le message via WebSocket avec l'ID de conversation
        socket.emit('message', { 
            content: sanitizedMessage, 
            model: selectedModel,
            conversationId 
        });
        
        // Timeout de sécurité pour gérer les problèmes de réponse
        setTimeout(() => {
            setMessages((prev) => {
                if (prev.some(m => m.sender === 'ia' && m.content === 'Composing...')) {
                    return [
                        ...prev.filter(m => m.content !== 'Composing...'),
                        {
                            sender: 'ia',
                            content: 'Error: No response received after 30 seconds',
                            type: 'text',
                            modelName: selectedModel,
                            conversationId,
                            timestamp: new Date().toISOString(),
                            final: true
                        } as ExtendedMessage,
                    ];
                }
                return prev;
            });
            setIsLoading(false);
        }, 30000);
        
    } catch (error) {
        const err = error as AxiosError;
        let errorMessage = 'Server error: Check the API connection';
        
        // Gestion des erreurs spécifiques
        if (err.code === 'ECONNABORTED') errorMessage = 'Request timeout';
        if (err.response?.status === 401) {
            setIsAuthenticated(false);
            document.cookie = 'token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
            errorMessage = 'Session expired';
        } else if (err.response?.status === 500) {
            errorMessage = 'Internal server error';
        }
        
        console.error('Error sending message:', err);
        
        // Mettre à jour l'UI avec le message d'erreur
        setMessages((prev) => [
            ...prev.filter((m) => m.content !== 'Composing...'),
            {
                sender: 'ia',
                content: errorMessage,
                type: 'text',
                modelName: selectedModel,
                conversationId,
                timestamp: new Date().toISOString(),
                final: true
            } as ExtendedMessage,
        ]);
        
        setIsLoading(false);
    }
};

export default apiClient;